<script lang="ts">
  // One review draft, managed in place wherever drafts are listed (the
  // Review view's unsent drafts).
  //
  // The header says which draft it is: what it is and where it goes, the
  // snapshot it was left on, a quiet link that opens it in Files, and its
  // age. Then where it came from, the code it names (highlighted as the
  // diff is) and its text. The footer holds what is done to it: Edit and
  // Discard at the start, and at the end, alone, the card's main action,
  // a checkbox that includes it in the review being prepared, with what
  // the publish does with it when that is not simply "sends it". A draft
  // that is included looks it as a whole (edge and tint), for a reader
  // scanning many.
  import { commentKeys, keyboard } from '../keys/keyboard.svelte';
  import type { ReviewDraft } from '../api/server';
  import { subject, type PrStore } from '../state/pr.svelte';
  import { anchorCommit, isIncluded, mirroredComment, originText, stateText, type Fate } from '../logic/inbox';
  import { parseOffDiff, replaceOffDiffText } from '../logic/offdiff';
  import { contextLines, type ContextLine } from '../state/codecontext';
  import { loadDraft, mergeText, saveDraft } from '../state/drafts';
  import { relTime } from '../logic/time';
  import { onDestroy, tick, untrack } from 'svelte';
  import { tellers } from '../state/pending.svelte';
  import Html from './Html.svelte';
  import PendingTag from './PendingTag.svelte';
  import SaveButton from './SaveButton.svelte';
  import SnapshotChip from './SnapshotChip.svelte';

  interface Props {
    pr: PrStore;
    draft: ReviewDraft;
    /** The keyboard cursor is on it. */
    selected?: boolean;
    /** Its text is being edited in place. */
    editing?: boolean;
    /** What becomes of it at the next publish, where the list knows (the Review view). */
    fate?: Fate | null;
    /** Why nothing on it can be changed now (the list is out of date, or being published), or null. */
    locked?: string | null;
    /** A publish is sending it. */
    sending?: boolean;
    /** Where "Open in Files" leads: its line, or its thread. */
    href: string;
    onselect?: () => void;
    onjump?: () => void;
    ondiscard?: () => void;
    /** Its include control was pressed: tick it, or untick a ticked one. */
    oninclude?: () => void;
    /** Its text was saved: the write that saves it. */
    onedit?: (write: Promise<unknown>) => void;
  }
  let { pr, draft, selected = false, editing = $bindable(false), fate = null, locked = null, sending = false, href, onselect, onjump, ondiscard, oninclude, onedit }: Props = $props();

  const t = $derived(draft.target);
  const offDiff = $derived(parseOffDiff(draft.body));
  /** The text as written (an off-diff draft's header and marker are not part of it). */
  const text = $derived(offDiff?.text ?? draft.body);
  const busy = $derived(pr.writes.on(subject.draft(draft.id)));
  const included = $derived(isIncluded(draft));
  const off = $derived(pr.draftsOff || locked !== null);
  const offTitle = $derived(pr.draftsOff ? (pr.draftsOffText ?? undefined) : (locked ?? undefined));
  /** Where it came from, when not written here. */
  const origin = $derived(originText(draft));
  /** Its comment is in the viewer's pending review on GitHub: discarding it deletes that comment there. */
  const onGitHub = $derived(mirroredComment(draft) !== null);

  /** What the next publish does with it, beside its include control: where a ticked one goes, and why one waits. */
  const going = $derived.by(() => {
    if (fate === 'conversation') return { text: 'goes to the conversation', why: 'Its line is outside the diff, so GitHub takes it only as a conversation comment.' };
    if (fate === 'later') return { text: 'waits for a later review', why: 'It is on another commit than this review, and a review covers one commit.' };
    return null;
  });

  /** The thread a reply answers. */
  const thread = $derived(t.kind === 'reply' ? pr.threads.find((x) => x.id === t.thread_id) ?? null : null);

  /** The lines it names: first, last, side and the commit they are read at. */
  const span = $derived.by(() => {
    if (t.kind === 'line') return { from: t.start_line ?? t.line, to: t.line, side: t.side, commit: t.commit_sha };
    if (t.kind === 'conversation') return { from: t.line, to: t.line, side: t.side, commit: t.commit_sha };
    if (t.kind === 'file' && offDiff) {
      const m = offDiff.marker;
      return { from: m.start ?? m.line, to: m.line, side: m.side, commit: m.commit };
    }
    if (t.kind === 'reply' && thread) {
      const line = thread.originalLine ?? thread.line;
      const commit = thread.comments.nodes[0]?.originalCommit?.oid ?? pr.pr?.headRefOid ?? null;
      if (line && commit) return { from: thread.originalStartLine ?? line, to: line, side: thread.diffSide === 'LEFT' ? 'LEFT' : 'RIGHT', commit } as const;
    }
    return null;
  });

  const where = $derived.by(() => {
    const lines = span ? (span.from < span.to ? `lines ${span.from}–${span.to}` : `line ${span.to}`) + (span.side === 'LEFT' ? ' (old)' : '') : null;
    switch (t.kind) {
      case 'line':
        return { kind: 'Comment', lines };
      case 'file':
        return { kind: 'File comment', lines: lines ?? 'file' };
      case 'reply':
        return { kind: 'Reply', lines };
      case 'conversation':
        return { kind: 'To the conversation', lines };
      case 'pr':
        return { kind: 'On the pull request', lines: null };
    }
  });
  /** Where it is, in words: its file, or the pull request for one on the whole of it. */
  const placeName = $derived(t.kind === 'pr' ? 'the pull request' : t.path.split('/').pop());

  /** What a screen reader says when the keyboard cursor lands on it: "Comment, graphs.rs line 225, not included". */
  const label = $derived(
    `${where.kind}, ${placeName}${where.lines && where.lines !== 'file' ? ' ' + where.lines : ''}, ${stateText(draft)}${going ? ', ' + going.text : ''}${origin ? ', ' + origin.label : ''}`,
  );
  /** What its box is called: the same place, so each box in the list has its own name. */
  const boxLabel = $derived(`Include in this review: ${where.kind.toLowerCase()} on ${placeName}${where.lines && where.lines !== 'file' ? ' ' + where.lines : ''}`);

  let context: ContextLine[] | null | 'loading' = $state('loading');
  $effect(() => {
    const s = span;
    const path = t.path;
    // The old side of a line comment is the base's text.
    const commit = s ? (s.side === 'LEFT' && t.kind === 'line' ? (pr.pr?.baseRefOid ?? s.commit) : s.commit) : null;
    if (!s || !commit) {
      context = null;
      return;
    }
    let live = true;
    context = 'loading';
    void contextLines(pr.owner, pr.repo, path, commit, s.from, s.to).then((c) => {
      if (live) context = c;
    });
    return () => (live = false);
  });

  // ---------------------------------------------------------------- editing in place

  let card: HTMLElement | undefined = $state();
  // Closing the editor (saved or cancelled) gives focus back to the card, not to the page.
  let wasEditing = false;
  /** Counts openings of the editor: a save answered after it was closed is not the editor opened since. */
  let opened = 0;
  $effect(() => {
    const now = editing;
    if (!wasEditing && now) opened++;
    if (wasEditing && !now)
      void tick().then(() => {
        if (!document.activeElement || document.activeElement === document.body || card?.contains(document.activeElement)) card?.focus();
      });
    wasEditing = now;
  });

  let value = $state('');
  let area: HTMLTextAreaElement | undefined = $state();
  /** Unsent edit text, kept like every other comment being typed (the same box as the note in the code). */
  const editKey = $derived(`held:${draft.id}`);
  /** How saving the edit stands (see `Writes.box`): the editor stays open through it, and tells it. */
  const editSave = $derived(pr.writes.box(editKey));
  /** A write on the draft that the open editor does not tell itself: the card is dimmed and tagged. */
  const shownBusy = $derived(editing && editSave.at === 'saving' ? null : busy);
  // svelte-ignore state_referenced_locally (the store is the card's for its life)
  const mine = tellers(pr.writes);
  onDestroy(mine.release);

  // Opening the editor: the text, or an edit left unsent earlier; while open, a failed edit given up comes back.
  $effect(() => {
    if (!editing) return;
    const key = editKey;
    untrack(() => {
      const original = text;
      value = original;
      void (async () => {
        // An edit being sent, or offered for Retry, is not offered again (see Writes.holds).
        const saved = await loadDraft(key, { outbox: !pr.writes.holds(key) });
        if (!editing || key !== editKey) return;
        // An edit that was not saved is told here, with its text, and no longer by the toast.
        const failed = pr.writes.box(key);
        if (failed.at === 'failed') mine.tell(key);
        const unsent = failed.at === 'failed' ? mergeText(saved, failed.text ?? '') : saved;
        if (unsent && value === original) value = unsent;
        await tick();
        area?.focus();
      })();
    });
    const stop = pr.writes.listen(key, (back) => (value = mergeText(value, back)));
    return () => {
      stop();
      mine.release();
    };
  });

  /**
   * Save the edit. From the press the editor is saving: its button says so
   * and takes no second press. Once the server has the edit the editor
   * closes on the new text. If saving fails the editor stays as it was,
   * says why beside the button, and sends again on the next press.
   */
  function save() {
    if (!value.trim() || busy || off) return;
    const sent = value;
    const editor = opened;
    const body = offDiff ? replaceOffDiffText(draft.body, sent) : sent;
    mine.tell(editKey);
    const write = pr.editDraft(draft.id, body, { key: editKey, text: sent });
    onedit?.(write);
    // Still the editor that sent it (not closed, or opened again, meanwhile).
    void write.then((ok) => {
      if (ok && editing && opened === editor) editing = false;
    });
  }

  // Mod+Enter sends, Esc closes (never mid-composition).
  const keys = commentKeys((c) => {
    if (c === 'submit') void save();
    else editing = false;
  });
</script>

<!-- svelte-ignore a11y_click_events_have_key_events, a11y_no_noninteractive_element_interactions (a click moves the keyboard cursor; the keys are the list's) -->
<article
  bind:this={card}
  class="draft-card"
  class:selected
  class:included
  class:is-pending={!!shownBusy || sending}
  aria-busy={busy || sending ? 'true' : undefined}
  aria-current={selected ? 'true' : undefined}
  data-draft={draft.id}
  tabindex="-1"
  aria-label={label}
  onclick={onselect}
>
  <header class="draft-head">
    <span class="draft-where"><b>{where.kind}</b>{#if where.lines} · {where.lines}{/if}</span>
    {#if draft.author === 'agent'}<span class="badge agent" title="Proposed by an agent: sent only if you include it">agent</span>{/if}
    {#if t.kind !== 'pr'}<SnapshotChip
      {pr}
      commit={anchorCommit(draft) ?? (t.kind === 'reply' ? null : t.commit_sha)}
      at={draft.created_at}
      path={t.path}
      line={span && span.side === 'RIGHT' ? span.to : null}
      outdated={{ anchored: anchorCommit(draft) !== null }}
      link
    />{/if}
    <span class="draft-end">
      {#if shownBusy || sending}<span class="draft-busy"><PendingTag label={shownBusy?.label ?? 'Publishing'} /></span>{/if}
      <!-- Navigation, not an action on the draft: a link (a new tab opens it too); a plain click is the list's own command. -->
      <a
        class="draft-open"
        {href}
        title={t.kind === 'pr' ? 'Open the Overview' : t.kind === 'reply' ? 'Open its thread in Files' : 'Open its line in Files'}
        onclick={(e) => {
          e.stopPropagation();
          if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
          e.preventDefault();
          onjump?.();
        }}>{t.kind === 'pr' ? 'Open the Overview' : 'Open in Files'}</a
      >
      <span class="muted draft-age" title={draft.updated_at}>{relTime(draft.updated_at)}</span>
    </span>
  </header>
  {#if origin}
    <p class="draft-origin">
      <span class="origin-from">{origin.label}</span>{#if origin.note}<span class="origin-note" title={origin.why}>{origin.note}</span>{/if}
    </p>
  {/if}

  {#if context === 'loading'}
    <div class="draft-code loading" aria-hidden="true"></div>
  {:else if context?.length}
    <!-- The lines it names, highlighted as in the diff. -->
    <pre class="draft-code" aria-label="Code at {where.lines ?? t.path}">{#each context as l (l.n)}<span class="ln">{l.n}</span><code class="src">{@html l.html || ' '}</code>
{/each}</pre>
  {/if}

  {#if editing}
    <div class="composer draft-edit">
      <textarea bind:this={area} aria-label="Edit draft" bind:value={value} readonly={editSave.at === 'saving'} oninput={() => saveDraft(editKey, value)} {@attach keys}></textarea>
      <div class="row">
        <span class="where">{[keyboard.keysIn('field', { kind: 'submit' }) && `${keyboard.keysIn('field', { kind: 'submit' })} to save`, keyboard.keysIn('field', { kind: 'cancel' }) && `${keyboard.keysIn('field', { kind: 'cancel' })} to cancel`].filter(Boolean).join(', ')}. It stays {stateText(draft)}{onGitHub ? '; GitHub keeps its own text until you publish' : ''}.</span>
        <span class="spacer"></span>
        <button class="micro" onclick={() => (editing = false)}>{editSave.at === 'saving' ? 'Close' : 'Cancel'}</button>
        <SaveButton save={editSave} label="Save" disabled={!value.trim() || !!shownBusy || off} title={offTitle} onsave={save} />
      </div>
    </div>
  {:else}
    <Html md={text} />
  {/if}

  {#if !editing}
    <footer class="draft-actions">
      <button class="micro" disabled={!!busy || off} title={offTitle ?? 'Edit in place'} onclick={(e) => (e.stopPropagation(), onselect?.(), (editing = true))}>Edit</button>
      <button
        class="micro"
        disabled={!!busy || off}
        title={offTitle ?? (onGitHub ? 'Discard: also deletes this comment from your pending review on GitHub' : 'Discard; Undo in the notice brings it back')}
        onclick={(e) => (e.stopPropagation(), ondiscard?.())}>Discard</button
      >
      <!-- Kept for the viewer, out of the review (review/asides.ts): Set aside lists it apart, folded, and Bring back undoes it. -->
      <button
        class="micro"
        disabled={!!busy || pr.serverDataText !== null}
        title={pr.serverDataText ?? 'Keep it for yourself, out of this review (a conversation with your agent, say): listed under Set aside, never sent'}
        onclick={(e) => (e.stopPropagation(), pr.setAside(draft.id, true))}>Set aside</button
      >
      <span class="spacer"></span>
      <!-- The card's main action, alone at the end: what the next publish sends depends on it. -->
      <span class="draft-send">
        {#if going}<span class="draft-going" title={going.why}>{going.text}</span>{/if}
        {#if !included && pr.droppedBack(draft.id)}<span class="draft-why" title="You included it before new commits were pushed: check it against the new head, then include it again">new commits since you included it</span>{/if}
        <label class="draft-include" class:on={included} title={offTitle ?? (included ? 'Your next publish sends it. Press to take it out.' : 'It stays a draft until you include it.')}>
          <!--
            The box shows the draft's own state: a press asks the list to change it, as the key does, and
            the box then shows whatever the draft's state is (the press may be refused). The press is not
            cancelled: a browser puts a cancelled checkbox back as it was after the handler, over the state
            just shown.
          -->
          <input
            type="checkbox"
            checked={included}
            disabled={off}
            aria-label={boxLabel}
            onclick={(e) => {
              e.stopPropagation();
              const box = e.currentTarget;
              oninclude?.();
              void tick().then(() => (box.checked = included));
            }}
          />
          <span>{included ? 'Included in this review' : 'Include in this review'}</span>
        </label>
      </span>
    </footer>
  {/if}
</article>
